import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { FaTrashAlt } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { baseURL } from "../../../../Services/api/baseURL";
import useRbac from "../../../../Hooks/useRbac";

const apiBase = `${baseURL}/api`;

const authHeader = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  return { Authorization: token };
};

const SERVICE_LABELS = {
  admin: "Admin",
  product: "Product",
  order: "Order",
};

const METHOD_COLORS = {
  GET: "info",
  POST: "success",
  PUT: "warning",
  PATCH: "warning",
  DELETE: "danger",
};

const ActivityLogTable = () => {
  const rbac = useRbac();
  const canSeeAll = rbac.isSuperAdmin || rbac.can("employees.list");
  const canClear = rbac.isSuperAdmin || rbac.can("activities.delete");

  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [employeeFilter, setEmployeeFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Clear logs modal
  const [clearModal, setClearModal] = useState(false);
  const [clearMode, setClearMode] = useState("older_than_days");
  const [clearDays, setClearDays] = useState(30);
  const [clearFrom, setClearFrom] = useState("");
  const [clearTo, setClearTo] = useState("");
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: perPage };
      if (employeeFilter) params.employee_id = employeeFilter;
      if (moduleFilter) params.module = moduleFilter;
      if (serviceFilter) params.service = serviceFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await axios.get(`${apiBase}/activities`, {
        headers: authHeader(),
        params,
      });
      const data = res.data?.data || [];
      setRows(data);
      setTotal(res.data?.total || 0);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to load activities",
        text: err?.response?.data?.message || err.message,
      });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, employeeFilter, moduleFilter, serviceFilter, dateFrom, dateTo]);

  const loadEmployees = useCallback(async () => {
    if (!canSeeAll) return;
    try {
      const res = await axios.get(`${apiBase}/employees`, {
        headers: authHeader(),
        params: { limit: 100, status: "active" },
      });
      setEmployees(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load employees for filter:", err?.response?.data || err.message);
    }
  }, [canSeeAll]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const handleClear = async () => {
    const modeLabels = {
      all: "ALL activity logs permanently",
      date_range: `logs from ${clearFrom || "start"} to ${clearTo || "today"}`,
      older_than_days: `logs older than ${clearDays} days`,
    };
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Clear Activity Logs",
      text: `This will permanently delete ${modeLabels[clearMode]}. This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, clear it",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    setClearing(true);
    try {
      const body = { mode: clearMode };
      if (clearMode === "date_range") { body.date_from = clearFrom; body.date_to = clearTo; }
      if (clearMode === "older_than_days") body.days = clearDays;

      const res = await axios.delete(`${apiBase}/activities`, {
        headers: authHeader(),
        data: body,
      });
      Swal.fire({ icon: "success", title: `${res.data.deleted} log(s) cleared`, timer: 2000, showConfirmButton: false });
      setClearModal(false);
      setPage(1);
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed to clear logs", text: err?.response?.data?.message || err.message });
    } finally {
      setClearing(false);
    }
  };

  const onReset = () => {
    setPage(1);
    setEmployeeFilter("");
    setModuleFilter("");
    setServiceFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const columns = useMemo(() => [
    {
      name: "Employee",
      selector: (r) => r.employee_name,
      cell: (r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.employee_name}</div>
          <small className="text-muted">{r.employee_email}</small>
        </div>
      ),
      grow: 2,
    },
    {
      name: "Action",
      selector: (r) => r.action,
      cell: (r) => <span style={{ fontWeight: 500 }}>{r.action}</span>,
      width: "200px",
    },
    {
      name: "Description",
      selector: (r) => r.description,
      cell: (r) => (
        <span title={r.description} style={{ color: "#555", fontSize: "13px" }}>
          {r.description || "—"}
        </span>
      ),
      grow: 4,
      wrap: true,
    },
    {
      name: "Module",
      selector: (r) => r.module,
      cell: (r) => (
        <span style={{ textTransform: "capitalize" }}>{r.module}</span>
      ),
      width: "120px",
    },
    {
      name: "Service",
      selector: (r) => r.service,
      cell: (r) => (
        <Badge color={r.service === "admin" ? "primary" : r.service === "order" ? "warning" : "success"}>
          {SERVICE_LABELS[r.service] || r.service}
        </Badge>
      ),
      width: "100px",
    },
    {
      name: "Method",
      selector: (r) => r.http_method,
      cell: (r) => (
        <Badge color={METHOD_COLORS[r.http_method] || "secondary"}>
          {r.http_method}
        </Badge>
      ),
      width: "90px",
    },
    {
      name: "Status",
      selector: (r) => r.status_code,
      cell: (r) => (
        <Badge color={r.status_code < 300 ? "success" : r.status_code < 400 ? "info" : "danger"}>
          {r.status_code}
        </Badge>
      ),
      width: "80px",
    },
    {
      name: "Date",
      selector: (r) => r.createdAt,
      format: (r) => moment(r.createdAt).format("DD MMM YYYY, HH:mm"),
      sortable: true,
      width: "170px",
    },
  ], []);

  return (
    <Fragment>
      <Row className="mb-3 align-items-center">
        <Col md="10">
          {/* <h4 className="mb-0">
            <FaHistory className="me-2" />
            Activity Log
          </h4> */}
          <h5 className="fw-bolder">Activity Log</h5>
        </Col>
        {canClear && (
          <Col md="2" className="text-end">
            <Button color="danger" className='d-flex align-items-center ms-3' outline onClick={() => setClearModal(true)}>
              <FaTrashAlt className="me-1" /> Clear Logs
            </Button>
          </Col>
        )}
      </Row>

      <Row className="mb-3 g-2">
        {canSeeAll && (
          <Col md="3">
            <Input
              type="select"
              value={employeeFilter}
              onChange={(e) => { setPage(1); setEmployeeFilter(e.target.value); }}
            >
              <option value="">All employees</option>
              {employees.map((e) => (
                <option key={e._id} value={e._id}>
                  {e.first_name} {e.last_name || ""} ({e.email})
                </option>
              ))}
            </Input>
          </Col>
        )}

        {canSeeAll && (
          <Col md="2">
            <Input
              type="select"
              value={serviceFilter}
              onChange={(e) => { setPage(1); setServiceFilter(e.target.value); }}
            >
              <option value="">All services</option>
              <option value="admin">Admin</option>
              <option value="product">Product</option>
              <option value="order">Order</option>
            </Input>
          </Col>
        )}

        <Col md="2">
          <Input
            placeholder="Module (e.g. brand)"
            value={moduleFilter}
            onChange={(e) => { setPage(1); setModuleFilter(e.target.value); }}
          />
        </Col>

        <Col md="2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
            placeholder="From date"
          />
        </Col>

        <Col md="2">
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
            placeholder="To date"
          />
        </Col>

        <Col md="1">
          <Button color="secondary" outline onClick={onReset} style={{ whiteSpace: "nowrap" }}>
            Reset
          </Button>
        </Col>
      </Row>

      <DataTable
        columns={columns}
        data={rows}
        progressPending={loading}
        pagination
        paginationServer
        paginationDefaultPage={page}
        paginationPerPage={perPage}
        paginationTotalRows={total}
        onChangePage={(p) => setPage(p)}
        onChangeRowsPerPage={(pp, p) => { setPerPage(pp); setPage(p); }}
        highlightOnHover
        persistTableHead
        noDataComponent={
          <div className="p-4 text-muted text-center">No activity records found.</div>
        }
      />

      {/* Clear Logs Modal */}
      <Modal isOpen={clearModal} toggle={() => setClearModal(false)}>
        <ModalHeader toggle={() => setClearModal(false)}>
          <FaTrashAlt className="me-2 text-danger" />
          Clear Activity Logs
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="fw-semibold mb-2">Clear mode</Label>
            <div className="d-flex flex-column gap-2">
              <Label check className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                <Input
                  type="radio"
                  name="clearMode"
                  value="older_than_days"
                  checked={clearMode === "older_than_days"}
                  onChange={() => setClearMode("older_than_days")}
                />
                Older than X days (auto-clean)
              </Label>
              <Label check className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                <Input
                  type="radio"
                  name="clearMode"
                  value="date_range"
                  checked={clearMode === "date_range"}
                  onChange={() => setClearMode("date_range")}
                />
                Specific date range
              </Label>
              <Label check className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                <Input
                  type="radio"
                  name="clearMode"
                  value="all"
                  checked={clearMode === "all"}
                  onChange={() => setClearMode("all")}
                />
                <span className="text-danger fw-semibold">Clear all logs entirely</span>
              </Label>
            </div>
          </FormGroup>

          {clearMode === "older_than_days" && (
            <FormGroup>
              <Label>Delete logs older than (days)</Label>
              <Input
                type="number"
                min={1}
                value={clearDays}
                onChange={(e) => setClearDays(e.target.value)}
                placeholder="e.g. 30"
              />
              <small className="text-muted">Logs created more than {clearDays} day(s) ago will be deleted.</small>
            </FormGroup>
          )}

          {clearMode === "date_range" && (
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>From date</Label>
                  <Input type="date" value={clearFrom} onChange={(e) => setClearFrom(e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>To date</Label>
                  <Input type="date" value={clearTo} onChange={(e) => setClearTo(e.target.value)} />
                </FormGroup>
              </Col>
            </Row>
          )}

          {clearMode === "all" && (
            <div className="alert alert-danger mb-0">
              This will permanently delete <strong>every</strong> activity log record. This cannot be undone.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setClearModal(false)} disabled={clearing}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleClear} disabled={clearing}>
            {clearing ? "Clearing…" : "Clear Logs"}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ActivityLogTable;
